import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';

import { plural } from '../../../../../shared/helpers';
import { window } from '../../../../globals';
import LabeledCheckbox from '../../../generic/Forms/LabeledCheckbox';
import PropagateModal from '../../shared/SettingsPropagateModal';
import type { NotificationsProjectSettings } from '../../../../../shared/settings/project/notifications';

import { usePropagateNotifications } from './hooks';

type Props = {
  settings: NotificationsProjectSettings;
  onHide: () => void;
  isOpen: boolean;
};

const getPropagateNotificationMessage = (settings: NotificationsProjectSettings): string => {
  const notificationDefaultEmailScanUserCount = settings.notificationDefaultEmailScanUsers.length;

  switch (settings.notificationDefaultEmailScanUserType) {
    case 'custom':
      return `Enable email notifications for all projects for ${notificationDefaultEmailScanUserCount} ${plural(
        notificationDefaultEmailScanUserCount,
        'user',
      )}`;
    case 'current':
      return 'Enable email notifications for all projects for current user';
    default:
      // handle 'all'
      return 'Enable email notifications for all projects for all users';
  }
};

const PropagateNotificationModal: VoidFunctionComponent<Props> = ({ settings, onHide, isOpen }) => {
  const {
    isPropagateEmailNotificationsEnabled,
    togglePropagateEmailNotificationsEnabled,
    isPropagateSlackNotificationsEnabled,
    togglePropagateSlackNotificationsEnabled,
    isPropagateApiWebhookNotificationsEnabled,
    togglePropagateApiWebhookNotificationsEnabled,
    isLoading,
    errors,
    onPropagateSettings,
    isSaveButtonDisabled,
  } = usePropagateNotifications(window.user?.organizationId as number, onHide);
  return (
    <PropagateModal
      settingsCategory="notification"
      isOpen={isOpen}
      errors={errors}
      onHide={onHide}
      onSave={onPropagateSettings}
      isSaveButtonDisabled={isSaveButtonDisabled}
      isSaveButtonLoading={isLoading}
    >
      <LabeledCheckbox
        label={
          _.isEmpty(settings.notificationDefaultEmailScanUsers)
            ? 'Disable email notifications for all projects'
            : getPropagateNotificationMessage(settings)
        }
        isChecked={isPropagateEmailNotificationsEnabled}
        onChange={togglePropagateEmailNotificationsEnabled}
        isRippleDisabled
      />
      <LabeledCheckbox
        label={
          settings.notificationDefaultSlackScan
            ? 'Enable slack notifications for all projects'
            : 'Disable slack notifications'
        }
        isChecked={isPropagateSlackNotificationsEnabled}
        onChange={togglePropagateSlackNotificationsEnabled}
        isRippleDisabled
      />
      <LabeledCheckbox
        label={
          settings.notificationDefaultApiWebhookScan
            ? 'Enable API webhook notifications for all projects'
            : 'Disable API webhook notifications'
        }
        isChecked={isPropagateApiWebhookNotificationsEnabled}
        onChange={togglePropagateApiWebhookNotificationsEnabled}
        isRippleDisabled
      />
    </PropagateModal>
  );
};

export default PropagateNotificationModal;
